import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Image, ScrollView, View } from 'react-native';
import MD from 'react-native-markdown-display';

import styles from './vocabulary-complete.style';
import { useFont } from '../../../hooks/lesson-player.context';
import { useActivityContext } from '../../../hooks/use-activity.context';
import { useErrorSound } from '../../../hooks/use-error-sound';
import { useSuccessSound } from '../../../hooks/use-success-sound';
import { arrayShuffle } from '../../../utils/array';
import { BlankPicker } from '../../blank-picker/blank-picker.component';
import { CheckButton } from '../../check-button/check-button';
import { ContinueButton } from '../../continue-button/continue-button';
import { Instruction } from '../../instruction/instruction.component';
import { ThemedText } from '../../themed-text';
import { ThemedView } from '../../themed-view';
import { Card } from '../card/card';

const OPTIONS_PER_PICKER = 4;
// TODO: remove this when react-native-markdown-display is updated
const MDany = MD as any;

export function VocabularyComplete() {
  const {
    currentActivity: activity,
    goNext,
    isCurrentActivityComplete: isComplete,
    completeCurrentActivity: completeActivity,
  } = useActivityContext();
  const correctSound = useSuccessSound();
  const incorrectSound = useErrorSound();
  const fonts = useFont();

  const [userAnswers, setUserAnswers] = useState<string[]>([]);

  const [isFeedbackVisible, setFeedbackVisible] = useState(false);

  const setUserAnswerForIndex = (index, answer: string) => {
    setUserAnswers((answers) => [
      ...answers.slice(0, index),
      answer,
      ...answers.slice(index + 1),
    ]);
  };

  const answers = useMemo(() => {
    const answers: string[] =
      activity.text.match(/\[\[(.*?)\]\]/g)?.map((word) => word.slice(2, -2)) ??
      [];

    setUserAnswers(Array.from({ length: answers.length }, () => ''));
    return answers;
  }, [activity.text]);

  const hasAllAnswers =
    userAnswers.every((answer) => answer !== '') &&
    userAnswers.length === answers.length;

  const completeUserAnswersOnActivityComplete = useCallback(() => {
    if (!isComplete) {
      return;
    }

    setUserAnswers([...answers]);
    setFeedbackVisible(true);
  }, [isComplete, answers]);

  const resetIncorrectUserAnswers = () => {
    const newUserAnswers = userAnswers.map((answer, index) =>
      answer === answers[index] ? answer : ''
    );

    setUserAnswers(newUserAnswers);
  };

  useEffect(() => {
    completeUserAnswersOnActivityComplete();
  }, [completeUserAnswersOnActivityComplete]);

  const options = useMemo(() => {
    let options = [...answers];
    if (activity?.distractor) {
      const distractors =
        activity.distractor
          .match(/\[\[(.*?)\]\]/g)
          ?.map((word) => word.slice(2, -2)) ?? [];
      options = [...options, ...distractors];
    }

    return options;
  }, [activity, answers]);

  const pickOptionsForAnswer = useCallback(
    (numberOfOptions, correctAnswer) => {
      const optionsWithoutCorrectAnswer = options.filter(
        (option) => option !== correctAnswer
      );
      return arrayShuffle([
        correctAnswer,
        ...arrayShuffle(optionsWithoutCorrectAnswer).slice(
          0,
          numberOfOptions - 1
        ),
      ]);
    },
    [options]
  );

  const pickerOptions = useMemo(() => {
    return answers.map((answer) =>
      pickOptionsForAnswer(OPTIONS_PER_PICKER, answer)
    );
  }, [answers, pickOptionsForAnswer]);

  const textItems = useMemo(() => {
    if (!activity?.text) {
      return null;
    }

    let blankIndex = 0;
    const text = activity.text.replace(/\[\[.*?\]\]/g, () => {
      return `[![]()](${blankIndex++})`;
    });

    const rules = {
      softbreak: (node, _children, _parent, styles) => {
        return (
          <ThemedText key={node.key} style={styles.hardbreak}>
            {'\n'}
          </ThemedText>
        );
      },
      blocklink: (node) => {
        const index = +node.attributes.href;
        return (
          <BlankPicker
            containerStyle={styles.blank}
            key={node.key}
            index={index}
            options={pickerOptions[index]}
            isCorrect={answers[index] === userAnswers[index]}
            isResultFinal={isComplete}
            isDisabled={isComplete}
            isResultVisible={isFeedbackVisible}
            word={userAnswers[index]}
            setResult={(index, selectedWord) =>
              setUserAnswerForIndex(index, selectedWord)
            }
          />
        );
      },
      text: (node, _children, _parent, styles, inheritedStyles = {}) => {
        const { content } = node;
        const text = content.trim().split(' ');

        if ((inheritedStyles as any).fontWeight === 'bold') {
          inheritedStyles = { ...fonts.primary.bold, ...inheritedStyles };
        }

        if ((inheritedStyles as any).fontStyle === 'italic') {
          inheritedStyles = { ...fonts.primary.italic, ...inheritedStyles };
        }

        return text.map((word, index) => (
          <ThemedText
            style={[inheritedStyles, styles.text]}
            key={`${node.key}-${word}-${index}`}
          >
            {word}{' '}
          </ThemedText>
        ));
      },
      textgroup: (_, children) => {
        return children;
      },
    };

    const markdownStyle = {
      body: {
        flexShrink: 1,
      },
    };

    return (
      <MDany style={markdownStyle} rules={rules}>
        {text}
      </MDany>
    );
  }, [
    activity.text,
    isFeedbackVisible,
    userAnswers,
    answers,
    pickerOptions,
    isComplete,
    fonts,
  ]);

  const verifyActivity = () => {
    const isCorrect = userAnswers.every(
      (answer, index) => answer === answers[index]
    );

    setFeedbackVisible(true);

    if (isCorrect) {
      completeActivity();
      correctSound.play();
    } else {
      incorrectSound.play();
      resetIncorrectUserAnswers();
    }
  };

  return (
    <>
      <ScrollView style={styles.section}>
        <Instruction text={activity.instructions} />
        {!!activity?.image && (
          <Image style={styles.image} source={{ uri: activity?.image }} />
        )}
        <ThemedView style={styles.blueBox} />
        <View style={styles.floatView}>
          <Card containerStyle={styles.card}>{textItems}</Card>
        </View>
      </ScrollView>

      <CheckButton
        isVisible={hasAllAnswers && !isComplete}
        onPress={verifyActivity}
      />
      {isComplete && <ContinueButton onPress={goNext} />}
    </>
  );
}
